import {FormControl, FormField} from 'platform/components';

import {memo, useMemo} from 'react';
import {useWatch} from 'react-hook-form';

import {isNilOrEmpty} from 'ramda-adjunct';

import {VehicleTypeEnumObject} from '@dms/api/shared';
import i18n from '@dms/i18n';
import {useVehicleModel} from '@dms/shared';

import {Nullish, RequiredTestIdProps} from 'shared/web';

import {BaseVehicle} from '../../core/types/BaseVehicle';
import {useStepFieldRegistration} from '../../kit/hooks/useStepFieldRegistration';

interface VehicleModelSelectProps extends RequiredTestIdProps {
  vehicleType: VehicleTypeEnumObject | null;
  isLoading?: boolean;
  onChange?: (value: string | Nullish) => void;
  onCreateOption: (value: string) => void;
  control: FormControl<BaseVehicle>;
  unknownModels?: Array<{value: string; label: string}>;
  isRequired?: boolean;
  isRecommended?: boolean;
  label?: string;
  vehicleMake: string | null;
}

export const VehicleModelSelect = memo<VehicleModelSelectProps>(
  (props: VehicleModelSelectProps) => {
    const [{groupedModelOptionsByMake: catalogModelOptions}, {isLoading: isLoadingModels}] =
      useVehicleModel(props.vehicleType, props.vehicleMake ? [props.vehicleMake] : null);

    const currentFormModelValue = useWatch({
      control: props.control,
      name: 'formModelFamily',
    });

    useStepFieldRegistration('formModelFamily');

    // Auto-detect if current form model value should be treated as unknown model
    const enhancedUnknownModels = useMemo(() => {
      const unknownOptions = props.unknownModels ?? [];

      if (!currentFormModelValue) {
        return unknownOptions;
      }

      const safeModelOptions = catalogModelOptions ?? [];

      // Check if current model value exists in catalog options
      const existsInCatalog = safeModelOptions.some((group) =>
        group.options.some((option) => option.value === currentFormModelValue)
      );

      // Check if current model value exists in unknown models
      const existsInUnknown = unknownOptions.some((model) => model.value === currentFormModelValue);

      // If it doesn't exist in either catalog or unknown models, add it to unknown models
      if (!existsInCatalog && !existsInUnknown) {
        const newModelOption = {
          value: currentFormModelValue,
          label: currentFormModelValue,
        };
        return [...unknownOptions, newModelOption];
      }

      return unknownOptions;
    }, [currentFormModelValue, catalogModelOptions, props.unknownModels]);

    // Merge catalog options with unknown models
    const modelOptions = useMemo(() => {
      const catalogOptions = catalogModelOptions ?? [];
      const unknownOptions = enhancedUnknownModels;

      if (unknownOptions.length === 0) {
        return catalogOptions;
      }

      // Filter out unknown models that already exist in catalog
      const newUnknownOptions = unknownOptions.filter(
        (unknownModel) =>
          !catalogOptions.some((catalogGroup) =>
            catalogGroup.options.some((option) => option.value === unknownModel.value)
          )
      );

      if (newUnknownOptions.length === 0) {
        return catalogOptions;
      }

      // If we have catalog options, add unknown models to the last group
      if (catalogOptions.length > 0) {
        const lastGroup = catalogOptions.at(-1);
        const updatedLastGroup = {
          ...lastGroup,
          options: [...(lastGroup?.options ?? []), ...newUnknownOptions],
        };
        return [...catalogOptions.slice(0, -1), updatedLastGroup];
      }

      // If no catalog options, create a group for unknown models
      return [
        {
          label: i18n.t('entity.vehicle.labels.model'),
          options: newUnknownOptions,
        },
      ];
    }, [catalogModelOptions, enhancedUnknownModels]);

    const isLoading = isLoadingModels || props.isLoading;

    return (
      <FormField
        type="creatableChoice"
        control={props.control}
        name="formModelFamily"
        label={props.label ?? i18n.t('entity.vehicle.labels.model')}
        isLoading={isLoading}
        options={modelOptions ?? []}
        onChange={
          props.onChange ? (newValue) => props.onChange?.(newValue as string | Nullish) : undefined
        }
        onCreateOption={props.onCreateOption}
        isDisabled={isNilOrEmpty(props.vehicleMake)}
        isRequired={props.isRequired}
        isRecommended={props.isRecommended}
        data-testid={props['data-testid']}
      />
    );
  }
);

VehicleModelSelect.displayName = 'VehicleModelSelect';
